import { clickAndCollectLocationQueryStore } from '@/hooks/order/clickAndCollectLocationQueryStore';
import { addressesAreEqual } from '@/utils/address';
import { usePrevious } from '@montugroup/circuit-shared';
import useUserProfileData from '@montugroup/circuit-shared/hooks/user/useGetUserProfile';
import type { IUserProfileResponse } from '@montugroup/circuit-shared/types/';
import type { Address } from '@montugroup/circuit-shared/types/address';
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

export const userAddressToAddress = (
  userResponse: IUserProfileResponse['user'],
  getUserState: () => null | any,
): Address => {
  const [addressLine1, addressLine2] = userResponse.address.split('$$$$') || [];
  const state = getUserState() || '';
  return {
    addressLine1,
    addressLine2,
    suburb: userResponse.city,
    postcode: userResponse.zip_code,
    state,
  };
};

const EMPTY: Address = {};

export default function useClickAndCollectLocationQuery() {
  const { data: userData, getUserState } = useUserProfileData();

  const raw = useSyncExternalStore(
    clickAndCollectLocationQueryStore.subscribe,
    clickAndCollectLocationQueryStore.getSnapshot,
    () => null,
  );

  const stored = useMemo(() => {
    return raw ? clickAndCollectLocationQueryStore.getParsed() ?? null : null;
  }, [raw]);

  const userAddressAsAddress = useMemo(() => {
    if (!userData?.user) {
      return null;
    }

    return userAddressToAddress(userData.user, getUserState);
  }, [userData?.user, getUserState]);

  const previousUserAddressAsAddress = usePrevious(userAddressAsAddress);
  const locationQuery: Address = stored ?? userAddressAsAddress ?? EMPTY;

  const setLocationQuery = useCallback((next: Address) => {
    const current = clickAndCollectLocationQueryStore.getParsed();
    if (current && addressesAreEqual(current, next)) {
      return;
    }

    clickAndCollectLocationQueryStore.set(next);
  }, []);

  useEffect(() => {
    if (!userAddressAsAddress) {
      return;
    }

    // If the user changes their profile address, update the default C&C search address
    // We need this check otherwise the initial load sets the stored query to the current user address which isn't what we want
    if (!!previousUserAddressAsAddress && previousUserAddressAsAddress !== userAddressAsAddress) {
      setLocationQuery(userAddressAsAddress);
    }
  }, [previousUserAddressAsAddress, setLocationQuery, stored, userAddressAsAddress]);

  const clearLocationQuery = useCallback(() => {
    clickAndCollectLocationQueryStore.clear();
  }, []);

  return { locationQuery, setLocationQuery, clearLocationQuery };
}
